/**
 * Shared impact types + small pure helpers that translate a user's dropdown
 * selections into the request shape `/impact/result` expects.
 *
 * No mock/generated data lives here — every displayed number comes from
 * either the live CDN dataset (`services/locationMetricsService`) or the
 * real `/impact/result` / `/locations/metrics` API responses. See
 * `ImpactPage.tsx` and `ConnectPage.tsx` for how a failed API call is
 * surfaced (an explicit "couldn't load" state, never a fabricated number).
 */
import { DEFAULT_GIFT_AMOUNT_ID, GIFT_AMOUNTS } from '../../../config/dropdowns';

export interface AreaMetrics {
  /** Unique id — FIPS (2-digit state, 5-digit county). */
  code: string;
  name: string;
  opportunityScore: number; // 0–100
  eligibleUnder18: number;
  medianIncome: number;
  receivingSNAP: number; // percent
  participation529: number; // percent
  childPovertyRate: number; // percent
  housingBurdened: number; // percent
}

export interface OverviewMetrics {
  eligibleChildrenUnder18: number;
  medianHouseholdIncome: number;
  participation529: number; // percent
  childPovertyRate: number; // percent
  economicMobility: 'Very Low' | 'Low' | 'Moderate' | 'High';
  childrenReceivingSNAP: number; // percent
  housingBurdened: number; // percent
  needleScore: number; // percent — "moving the needle on the savings gap"; == pctGapClosed
}

export interface ProjectionPoint {
  age: number;
  amount: number;
  /**
   * A band the API computes around `amount` — optional/defensive on the
   * frontend since its exact definition is still moving server-side. As of
   * the last live check, a deployed response's `upper[i]`/`lower[i]` were
   * literally the next/previous point's `amount` (0-clamped at the edges).
   * The backend has separately been moving toward upper = youngest-child-in-
   * bin / lower = oldest-child-in-bin (a real longest/shortest-runway
   * spread) — once that ships here, this comment should be updated to match
   * rather than assumed. Either way: render whatever the API sends, never
   * recompute it.
   */
  upper?: number;
  lower?: number;
}

export interface ImpactResult {
  overview: OverviewMetrics;
  areas: AreaMetrics[];
  projection: ProjectionPoint[];
  reachChildren: number;
  totalWealthSavings: number;
  perChild: number;
}

/**
 * Amount id → dollars, from the shared gift-amount config. When the amount
 * isn't set/recognized (e.g. left empty on the landing page), default to the
 * standard gift amount ($5M).
 */
export function amountToDollars(amountId: string | null): number {
  const found = GIFT_AMOUNTS.find((a) => a.id === amountId);
  if (found) return found.value;
  const fallback = GIFT_AMOUNTS.find((a) => a.id === DEFAULT_GIFT_AMOUNT_ID);
  return fallback?.value ?? 5_000_000;
}

/** Compact number formatting → "2.1M", "756K", "$56K". */
export function formatCompact(value: number, currency = false): string {
  const prefix = currency ? '$' : '';
  if (value >= 1_000_000) return `${prefix}${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${prefix}${Math.round(value / 1_000)}K`;
  return `${prefix}${value}`;
}

/**
 * A SNAP participation rate above 100% is a data artifact (small ACS sample
 * → large margin of error), not a real value — suppress it rather than show
 * a nonsensical percentage.
 */
export const SNAP_SUPPRESSED_NOTE = 'Value suppressed due to large statistical error';

/** SNAP rate display — "NA" (with an explanatory title) once it exceeds 100%. */
export function formatSnapRate(value: number): string {
  return value > 100 ? 'NA' : `${value}%`;
}
